// src/components/TransactionHistory/TransactionHistory.jsx
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, CreditCard, Calendar, AlertCircle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { createApi } from '../../services/api';
import MainLayout from '../Layout/MainLayout';

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const { token, signIn } = useAuth();
  const api = createApi(token);

  useEffect(() => {
    fetchBalance();
    fetchTransactions(1);
  }, [token]);

  const fetchBalance = async () => {
    try {
      const response = await api.getBalance();
      setCreditBalance(response.balance);
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  const fetchTransactions = async (page = 1) => {
    try {
      setIsLoading(true);
      const response = await api.getTransactionHistory();
      
      if (response.transactions) {
        // Transform the transactions to match our component's expected format
        const transformedTransactions = response.transactions.map(t => ({
          transaction_date: t.date, // Backend sends 'date', we use 'transaction_date'
          amount: t.amount,
          mpesa_receipt_number: t.receipt_number, // Backend sends 'receipt_number'
          is_processed: t.status === 'success' // Backend sends 'status' as 'success' or 'failed'
        }));
        
        setTransactions(transformedTransactions);
        setPagination({
          ...pagination,
          currentPage: 1,
          totalPages: 1,
          totalItems: transformedTransactions.length
        });
      } else {
        // Fallback if response format is unexpected
        setTransactions([]);
        setPagination({
          ...pagination,
          currentPage: 1,
          totalPages: 1,
          totalItems: 0
        });
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(`Failed to load transaction history. Error: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchTransactions(newPage);
    }
  };

  // Get transactions for current page or expanded view
  const displayedTransactions = isExpanded
    ? transactions
    : pagination.itemsPerPage < transactions.length 
      ? transactions.slice(0, pagination.itemsPerPage) 
      : transactions;

  // Generate an array of page numbers to display
  const getPageNumbers = () => {
    const { currentPage, totalPages } = pagination;
    const delta = 1; // Number of pages to show on each side of current page
    const range = [];
    
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    // Add first page
    if (currentPage - delta > 2) {
      range.unshift('...');
    }
    if (totalPages > 1) {
      range.unshift(1);
    }

    // Add last page
    if (currentPage + delta < totalPages - 1) {
      range.push('...');
    }
    if (totalPages > 1 && totalPages !== 1) {
      range.push(totalPages);
    }

    return range;
  };

  // Group transactions by date
  const groupedTransactions = displayedTransactions.reduce((groups, transaction) => {
    const date = new Date(transaction.transaction_date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    if (!groups[date]) {
      groups[date] = [];
    }
    
    groups[date].push(transaction);
    return groups;
  }, {});

  if (!token) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <div className="bg-white p-10 rounded-xl shadow-lg max-w-md w-full">
            <AlertCircle className="mx-auto h-16 w-16 text-red-500" />
            <h2 className="mt-6 text-2xl font-bold text-gray-800">Authentication Required</h2>
            <p className="mt-2 text-gray-600">
              Please sign in to view your transaction history.
            </p>
            <button
              onClick={signIn}
              className="mt-6 w-full inline-flex justify-center py-3 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Sign In with Google
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (isLoading) {
    return (
      <MainLayout creditBalance={creditBalance}>
        <div className="flex items-center justify-center h-full p-8">
          <div className="text-center">
            <RefreshCw className="mx-auto h-12 w-12 animate-spin text-blue-600" />
            <h2 className="mt-4 text-xl font-semibold text-gray-700">Loading Transactions...</h2>
            <p className="mt-2 text-gray-500">Please wait while we fetch your history.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout creditBalance={creditBalance}>
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <div className="bg-white p-10 rounded-xl shadow-lg max-w-md w-full">
            <AlertCircle className="mx-auto h-16 w-16 text-orange-500" />
            <h2 className="mt-6 text-2xl font-bold text-gray-800">Something Went Wrong</h2>
            <p className="mt-2 text-gray-600">{error}</p>
            <button
              onClick={() => fetchTransactions(1)}
              className="mt-6 w-full inline-flex justify-center py-3 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <RefreshCw className="mr-2 h-5 w-5" />
              Try Again
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const TransactionItem = ({ transaction }) => (
    <li className="py-4 px-6 hover:bg-gray-50 transition-colors duration-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className={`p-3 rounded-full mr-4 ${transaction.is_processed ? 'bg-green-100' : 'bg-yellow-100'}`}>
            <CreditCard className={`h-6 w-6 ${transaction.is_processed ? 'text-green-600' : 'text-yellow-600'}`} />
          </div>
          <div>
            <p className="font-semibold text-gray-800">MPESA Payment</p>
            <p className="text-sm text-gray-500">Receipt: {transaction.mpesa_receipt_number}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-bold text-lg text-gray-900">KES {transaction.amount}</p>
          <p className="text-sm text-gray-500 flex items-center justify-end">
            <Calendar className="h-4 w-4 mr-1.5" />
            {new Date(transaction.transaction_date).toLocaleDateString('en-US', {
              year: 'numeric', month: 'long', day: 'numeric'
            })}
          </p>
        </div>
      </div>
    </li>
  );

  const EmptyState = () => (
    <div className="text-center py-16 px-6">
      <CreditCard className="mx-auto h-16 w-16 text-gray-400" />
      <h3 className="mt-4 text-xl font-bold text-gray-800">No Transactions Yet</h3>
      <p className="mt-2 text-gray-500">Your payment history will appear here once you make a transaction.</p>
    </div>
  );

  const Pagination = () => (
    <div className="flex items-center justify-between bg-gray-50 px-6 py-3 border-t border-gray-200">
      <p className="text-sm text-gray-600">
        Showing <span className="font-medium">{((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}</span>
        - <span className="font-medium">{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}</span> of <span className="font-medium">{pagination.totalItems}</span> results
      </p>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => handlePageChange(pagination.currentPage - 1)}
          disabled={pagination.currentPage === 1}
          className="p-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="text-sm font-medium">
          Page {pagination.currentPage} of {pagination.totalPages}
        </span>
        <button
          onClick={() => handlePageChange(pagination.currentPage + 1)}
          disabled={pagination.currentPage === pagination.totalPages}
          className="p-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );

  return (
    <MainLayout creditBalance={creditBalance}>
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-700 px-6 py-5 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-white">Transaction History</h1>
              <p className="text-purple-100 mt-1">Review your recent payments and credit top-ups</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => fetchTransactions(1)}
                className="p-2 text-white rounded-full hover:bg-white/20 transition-colors"
                title="Refresh History"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 text-white rounded-full hover:bg-white/20 transition-colors"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {transactions.length > 0 ? (
            <div>
              <ul className="divide-y divide-gray-200">
                {displayedTransactions.map((transaction, index) => (
                  <TransactionItem key={index} transaction={transaction} />
                ))}
              </ul>
              {pagination.totalItems > pagination.itemsPerPage && !isExpanded && (
                <Pagination />
              )}
            </div>
          ) : (
            <EmptyState />
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default TransactionHistory;